import React from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, Tag, Tooltip, Button, Space } from 'antd';
import { PlusOutlined, CheckOutlined } from '@ant-design/icons';

// One colour per tax, so a glance at the column tells which taxes a product carries.
const TAX_COLORS = { iva: 'blue', ibee: 'orange' };
export const taxColor = (tax) => TAX_COLORS[tax] || 'purple';

/** A product's tags after removing some and adding others; an added tag replaces the one of its tax. */
export const nextTags = (current, add, remove, taxOf) => {
  let tags = current.filter((tag) => !remove.includes(tag));
  add.forEach((tag) => {
    tags = tags.filter((existing) => existing !== tag && taxOf.get(existing) !== taxOf.get(tag));
    tags.push(tag);
  });
  return [...new Set(tags)].sort();
};

/** The tags grouped by tax; a click on one calls onPick. `marked` tags show a check. */
const TagMenu = ({ tagsByTax, marked = [], onPick }) => {
  const { t } = useTranslation();
  return (
    <div style={{ maxWidth: 360, maxHeight: 360, overflowY: 'auto' }}>
      {Object.entries(tagsByTax).map(([tax, tags]) => (
        <div key={tax} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#787774', textTransform: 'uppercase', marginBottom: 4 }}>{tax}</div>
          <Space size={[4, 4]} wrap>
            {tags.map((tag) => (
              <Tag
                key={tag}
                color={marked.includes(tag) ? taxColor(tax) : undefined}
                onClick={() => onPick(tag)}
                style={{ cursor: 'pointer', marginInlineEnd: 0 }}
              >
                {marked.includes(tag) && <CheckOutlined style={{ marginRight: 4 }} />}
                {tag}
              </Tag>
            ))}
          </Space>
        </div>
      ))}
      <div style={{ fontSize: 11, color: '#787774' }}>{t('costTagsReplaceHint')}</div>
    </div>
  );
};

/** A product's tags as coloured chips: × removes one, + opens the tags to add (one per tax). */
export const TaxTagsCell = ({ tags, tagsByTax, taxOf, disabled, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  return (
    <Space size={[4, 4]} wrap style={{ maxWidth: 420 }}>
      {tags.map((tag) => (
        <Tooltip key={tag} title={tag} placement="top">
          <Tag
            color={taxColor(taxOf.get(tag))}
            closable={disabled ? false : { 'aria-label': t('costTagsRemoveTag', { tag }) }}
            onClose={(e) => { e.preventDefault(); onChange(tags.filter((x) => x !== tag)); }}
            style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', marginInlineEnd: 0 }}
          >
            {tag}
          </Tag>
        </Tooltip>
      ))}
      {!disabled && (
        <Popover
          trigger="click"
          open={open}
          onOpenChange={setOpen}
          placement="bottomLeft"
          content={(
            <TagMenu
              tagsByTax={tagsByTax}
              marked={tags}
              onPick={(tag) => {
                onChange(tags.includes(tag) ? tags.filter((x) => x !== tag) : nextTags(tags, [tag], [], taxOf));
                setOpen(false);
              }}
            />
          )}
        >
          <Tag style={{ borderStyle: 'dashed', cursor: 'pointer', background: 'transparent', marginInlineEnd: 0 }}
            aria-label={t('costTagsAdd')}>
            <PlusOutlined /> {tags.length === 0 ? t('costTagsAdd') : ''}
          </Tag>
        </Popover>
      )}
    </Space>
  );
};

/** Shown while products are selected: add a tag to all of them, remove one, or clear the selection. */
export const BulkTagsBar = ({ count, tagsByTax, presentTags, busy, onAdd, onRemove, onClear }) => {
  const { t } = useTranslation();
  const [openMenu, setOpenMenu] = React.useState(null);
  const presentByTax = Object.fromEntries(Object.entries(tagsByTax)
    .map(([tax, tags]) => [tax, tags.filter((tag) => presentTags.includes(tag))])
    .filter(([, tags]) => tags.length > 0));
  const menu = (kind, groups, onPick) => (
    <Popover
      trigger="click"
      open={openMenu === kind}
      onOpenChange={(open) => setOpenMenu(open ? kind : null)}
      placement="bottomLeft"
      content={<TagMenu tagsByTax={groups} onPick={(tag) => { setOpenMenu(null); onPick(tag); }} />}
    >
      <Button size="small" disabled={busy} danger={kind === 'remove'} icon={kind === 'add' ? <PlusOutlined /> : null}>
        {t(kind === 'add' ? 'costTagsAdd' : 'costTagsRemove')}
      </Button>
    </Popover>
  );
  return (
    <div role="toolbar" style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', marginBottom: 8,
      background: '#f5f8ff', border: '1px solid #d6e4ff', borderRadius: 8,
    }}>
      <strong>{t('costTagsSelected', { n: count })}</strong>
      {menu('add', tagsByTax, onAdd)}
      {Object.keys(presentByTax).length > 0 && menu('remove', presentByTax, onRemove)}
      <Button size="small" type="link" onClick={onClear} disabled={busy}>{t('costTagsClearSelection')}</Button>
    </div>
  );
};
